import {
  KEEP_NUMBER_OPTION__KEEP,
  KEEP_NUMBER_OPTION__KEEP_OTHER,
} from '~/models/checkout/checkout_store/NumberConfigCheckoutState.model';
import { SWISH_METHOD } from '~/models/checkout/PaymentMethods.model';
import type { HalebopRequestErrorData } from '~/repository/factory';

interface FieldContext {
  field: string;
  label?: string;
  name: string;
  value: string;
  form?: any;
}

interface FormFieldValidators {
  discountCodeNotEmpty: (value: string) => string | boolean;
  emailAddressContainsNoSwedishCharacters: (value: string) => string | boolean;
  emailCorrectFormat: (value: string) => string | boolean;
  emailNotEmpty: (value: string) => string | boolean;
  numberOptionStep2IsSelected: (value: string) => string | boolean;
  paymentOptionStep4IsSelected: (value: string) => string | boolean;
  phoneNumberCorrectFormat: (value: string) => string | boolean;
  phoneNumberForFamilyPlanNotAlreadySet: (
    number: string,
    context: FieldContext,
  ) => string | boolean;
  phoneNumberForFamilyPlanNotSameAsParent: (number: string) => string | boolean;
  phoneNumberNotEmpty: (value: string) => string | boolean;
  phoneNumberToKeepHasBeenValidatedBeforeStep2Submit: any; // setting any because can't manage to import necessary vee-validate types
  smsCodeCorrectFormat: (value: string) => string | boolean;
  smsCodeNotEmpty: (value: string) => string | boolean;
  ssnCorrectFormat: (value: string) => string | boolean;
  ssnForFamilyPlanVerified: (value: string) => Promise<string | boolean>;
  ssnNotEmpty: (value: string) => string | boolean;
  swishStep4NumberCorrectFormat: any;
  swishStep4NumberNotEmpty: any;
}

export type FormFieldValidatorKeys = keyof FormFieldValidators;

const _crossFieldValidateThatSwishMethodIsSelected = (
  ctx?: FieldContext,
): boolean => {
  return ctx?.form && Object.values(ctx?.form).includes(SWISH_METHOD);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default defineNuxtPlugin((_nuxtApp) => {
  const { $api } = useNuxtApp();
  const checkoutStore = useMyCheckoutStore();

  function discountCodeNotEmpty(value?: string) {
    return value?.trim() ? true : 'Vänligen ange en rabattkod';
  }

  function emailAddressContainsNoSwedishCharacters(value?: string) {
    if (value && /[åäöÅÄÖ]/.test(value)) {
      return 'E-postadressen kan för närvarande inte innehålla å, ä eller ö. Vänligen välj en annan adress';
    }

    return true;
  }

  function emailCorrectFormat(value?: string) {
    // from https://emailregex.com/
    const emailFormatRegex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return value && emailFormatRegex.test(value)
      ? true
      : 'Fyll i enligt format: example@halebop.se';
  }

  function emailNotEmpty(value?: string) {
    return value?.trim() ? true : 'Vänligen ange en e-postadress';
  }

  function numberOptionStep2IsSelected(value?: any) {
    return value?.trim()
      ? true
      : 'Gör ett val för telefonnummer för att fortsätta';
  }

  function paymentOptionStep4IsSelected(value?: any) {
    return value?.trim() ? true : 'Välj en betalningsmetod för att fortsätta';
  }

  function phoneNumberCorrectFormat(value?: string) {
    return value && /^07[0-9]{8}$/g.test(value)
      ? true
      : 'Fyll i enligt format: 0701234567';
  }

  const phoneNumberForFamilyPlanNotAlreadySet = (
    number: string,
    context: FieldContext,
  ): string | boolean => {
    const otherNumberConfigWithSamePhoneNumber = Object.values(
      checkoutStore.userState.numberConfigMap,
    ).filter(
      (item) =>
        item.keepNumberInputValue === number &&
        item.keepNumberInputFieldId !== context.name,
    );

    if (otherNumberConfigWithSamePhoneNumber.length > 0) {
      return 'Du kan inte flytta samma nummer som redan är angivit för flytt';
    }

    return true;
  };

  const phoneNumberForFamilyPlanNotSameAsParent = (
    number: string,
  ): string | boolean => {
    const parentMsisdn = Object.values(
      checkoutStore.userState.numberConfigMap,
    ).find((mapValue) => !!mapValue.parentMsisdn)?.parentMsisdn;

    if (!parentMsisdn) {
      return true;
    }

    const inputPhoneNumber = number;
    const reformattedInputPhoneNumber =
      number.charAt(0) === '0'
        ? `46${number.substring(1, number.length)}`
        : number;

    if (
      inputPhoneNumber === parentMsisdn ||
      reformattedInputPhoneNumber === parentMsisdn
    ) {
      return 'Du kan inte flytta huvudabonnemangets nummer';
    }

    return true;
  };

  const phoneNumberToKeepHasBeenValidatedBeforeStep2Submit = (
    _: string,
    context: FieldContext,
  ): string | boolean => {
    const errorMsg = 'Detta fält är obligatoriskt';

    // This validator can only be run during step 2 submit,
    // otherwise the inner submit for the phone number to show the
    // validation code will be stuck on this input field validator itself
    if (!checkoutStore.ui.numberConfigFormIsSubmitting) {
      return true;
    }

    const numberConfig = Object.values(
      checkoutStore.userState.numberConfigMap,
    ).find((item) => item.keepNumberInputFieldId === context.name);

    if (!numberConfig) {
      return errorMsg;
    }

    const keepNumberSelectedButSmsCodeNotSent =
      (numberConfig.keepNumberOption === KEEP_NUMBER_OPTION__KEEP ||
        numberConfig.keepNumberOption === KEEP_NUMBER_OPTION__KEEP_OTHER) &&
      !numberConfig.keepNumberCodeSent;

    if (keepNumberSelectedButSmsCodeNotSent) {
      return errorMsg;
    }

    return true;
  };

  function phoneNumberNotEmpty(value?: string) {
    return value?.trim() ? true : 'Vänligen ange ett mobilnummer';
  }

  function smsCodeCorrectFormat(value?: string) {
    return value && /^[0-9]{4}$/g.test(value) ? true : 'Koden är inte korrekt';
  }

  function smsCodeNotEmpty(value?: string) {
    return value?.trim() ? true : 'Vänligen ange valideringskoden';
  }

  function ssnCorrectFormat(value?: string) {
    return value && /^(19|20)?[0-9]{6}[- ]?[0-9]{4}$/g.test(value)
      ? true
      : 'Fyll i enligt format: ÅÅÅÅMMDDXXXX';
  }

  const ssnForFamilyPlanVerified = async (
    ssn: string,
  ): Promise<string | boolean> => {
    try {
      await $api.planModule.verifyFamilyPlanOwnerSsn(ssn);
      return true;
    } catch (error) {
      let errorMsg =
        'Personnumret går inte att använda. Familjeabonnemanget måste vara en myndig person och kan inte vara samma person som för huvudabonnemanget!';

      if (
        isNuxtError<HalebopRequestErrorData>(error) &&
        error.data?.errors?.SocialSecurityNumber
      ) {
        errorMsg = error.data.errors.SocialSecurityNumber;
      }

      return errorMsg;
    }
  };

  function ssnNotEmpty(value?: string) {
    return value?.trim() ? true : 'Vänligen ange ett personnummer';
  }

  function swishStep4NumberCorrectFormat(value?: string, ctx?: FieldContext) {
    if (_crossFieldValidateThatSwishMethodIsSelected(ctx)) {
      return phoneNumberCorrectFormat(value);
    }

    return true;
  }

  function swishStep4NumberNotEmpty(value?: string, ctx?: FieldContext) {
    if (_crossFieldValidateThatSwishMethodIsSelected(ctx)) {
      return value?.trim()
        ? true
        : 'Du måste fylla i giltigt telefonnummer för att betala med Swish';
    }

    return true;
  }

  const model: FormFieldValidators = {
    discountCodeNotEmpty,
    emailAddressContainsNoSwedishCharacters,
    emailCorrectFormat,
    emailNotEmpty,
    paymentOptionStep4IsSelected,
    phoneNumberCorrectFormat,
    phoneNumberForFamilyPlanNotAlreadySet,
    phoneNumberForFamilyPlanNotSameAsParent,
    phoneNumberNotEmpty,
    phoneNumberToKeepHasBeenValidatedBeforeStep2Submit,
    smsCodeCorrectFormat,
    smsCodeNotEmpty,
    ssnCorrectFormat,
    ssnForFamilyPlanVerified,
    ssnNotEmpty,
    swishStep4NumberCorrectFormat,
    swishStep4NumberNotEmpty,
    numberOptionStep2IsSelected,
  };

  return {
    provide: {
      validator: model,
    },
  };
});
